import React, { useState } from "react";
import { Link } from "react-router-dom";

// ─────────────────────────────────────────────
//  Project: Shopping Cart App
//  Chapter 10 · Projects
//  Author: Ajao Hazzan · Full-Stack Software Engineer
// ─────────────────────────────────────────────

const PRIMARY = "#61DAFB";
const BG = "https://images.unsplash.com/photo-1555066931-4365d14bab8c?w=1600&q=80";

/* ── Code snippets stored as template literals ── */
const CODE_1 = `shopping-cart/
  src/
    components/
      ProductCard/      ProductCard.jsx  ProductCard.css
      CartDrawer/       CartDrawer.jsx   CartItem.jsx
      SearchBar/        SearchBar.jsx
      CategoryFilter/   CategoryFilter.jsx
      EmptyState/       EmptyState.jsx
    context/
      CartContext.jsx   (cart state + actions)
    hooks/
      useCart.js        useProducts.js   useDebounce.js
    pages/
      ShopPage.jsx      CheckoutPage.jsx  OrderSuccessPage.jsx
    data/
      products.js       (mock product data)
    utils/
      currency.js       localStorage.js`;

const CODE_2 = `// context/CartContext.jsx
import { createContext, useContext, useReducer, useEffect } from 'react';

const CartContext = createContext(null);

const STORAGE_KEY = 'react-cart';

function cartReducer(state, action) {
  switch (action.type) {
    case 'ADD':    {
      const exists = state.items.find(i => i.id === action.item.id);
      const items  = exists
        ? state.items.map(i => i.id === action.item.id ? { ...i, qty: i.qty + 1 } : i)
        : [...state.items, { ...action.item, qty: 1 }];
      return { ...state, items };
    }
    case 'REMOVE': return { ...state, items: state.items.filter(i => i.id !== action.id) };
    case 'QTY':    return { ...state, items: state.items.map(i =>
      i.id === action.id ? { ...i, qty: Math.max(1, action.qty) } : i
    )};
    case 'CLEAR':  return { ...state, items: [] };
    case 'LOAD':   return { ...state, items: action.items };
    default:       return state;
  }
}

export function CartProvider({ children }) {
  const saved = (() => {
    try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || []; }
    catch { return []; }
  })();

  const [state, dispatch] = useReducer(cartReducer, { items: saved, isOpen: false });

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state.items));
  }, [state.items]);

  const value = {
    items:     state.items,
    isOpen:    state.isOpen,
    count:     state.items.reduce((s, i) => s + i.qty, 0),
    total:     state.items.reduce((s, i) => s + i.price * i.qty, 0),
    addItem:   (item)    => dispatch({ type: 'ADD', item }),
    removeItem:(id)      => dispatch({ type: 'REMOVE', id }),
    updateQty: (id, qty) => dispatch({ type: 'QTY', id, qty }),
    clearCart: ()        => dispatch({ type: 'CLEAR' }),
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be inside CartProvider');
  return ctx;
};`;


/* ── Styles ── */
const styles = {
  page:    { minHeight: "100vh", backgroundImage: `linear-gradient(rgba(10,12,30,0.94),rgba(10,12,30,0.97)),url(${BG})`, backgroundSize: "cover", backgroundAttachment: "fixed", color: "#fff", fontFamily: "'Segoe UI',sans-serif", paddingBottom: "60px" },
  header:  { background: "rgba(10,12,30,0.95)", borderBottom: "1px solid rgba(97,218,251,0.15)", padding: "18px 32px", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "12px", position: "sticky", top: 0, zIndex: 100 },
  badge:   { background: "rgba(97,218,251,0.12)", border: "1px solid rgba(97,218,251,0.3)", color: PRIMARY, padding: "4px 14px", borderRadius: "999px", fontSize: "0.75rem", fontWeight: 700, letterSpacing: "0.06em" },
  backBtn: { background: "rgba(97,218,251,0.1)", border: "1px solid rgba(97,218,251,0.25)", color: PRIMARY, padding: "8px 18px", borderRadius: "999px", cursor: "pointer", fontSize: "0.82rem", fontWeight: 600, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: "6px" },
  hero:    { padding: "48px 32px 24px", maxWidth: "900px", margin: "0 auto" },
  title:   { fontSize: "clamp(1.6rem,4vw,2.4rem)", fontWeight: 800, color: PRIMARY, marginBottom: "10px", lineHeight: 1.2 },
  desc:    { color: "rgba(255,255,255,0.55)", fontSize: "1rem", lineHeight: 1.7, maxWidth: "680px", marginBottom: "32px" },
  content: { maxWidth: "900px", margin: "0 auto", padding: "0 32px 40px" },
  section: { marginBottom: "36px" },
  sh:      { fontSize: "1.1rem", fontWeight: 700, color: "#fff", marginBottom: "10px", borderLeft: "3px solid " + PRIMARY, paddingLeft: "12px" },
  body:    { color: "rgba(255,255,255,0.72)", lineHeight: 1.85, fontSize: "0.95rem", marginBottom: "6px" },
  pre:     { background: "#0D0F1A", border: "1px solid rgba(97,218,251,0.15)", borderLeft: "3px solid " + PRIMARY, borderRadius: "10px", padding: "20px 22px", overflowX: "auto", margin: "14px 0", fontSize: "0.83rem", lineHeight: 1.7, color: "#CDD6F4", fontFamily: "Courier New,monospace", whiteSpace: "pre" },
  qBox:    { background: "rgba(97,218,251,0.04)", border: "1px solid rgba(97,218,251,0.12)", borderRadius: "10px", padding: "18px 22px", marginTop: "8px" },
  qTitle:  { color: PRIMARY, fontWeight: 700, fontSize: "0.82rem", letterSpacing: "0.08em", marginBottom: "10px" },
  qItem:   { color: "rgba(255,255,255,0.65)", lineHeight: 1.8, fontSize: "0.92rem", marginBottom: "4px" },
  footer:  { maxWidth: "900px", margin: "0 auto", padding: "24px 32px 0", borderTop: "1px solid rgba(255,255,255,0.07)", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "12px" },
  credit:  { color: "rgba(255,255,255,0.25)", fontSize: "0.75rem" },
};

/* ── Component ── */
export default function ProjectShoppingCartApp() {
  const [copied, setCopied] = useState(null);

  function copyCode(codeStr, idx) {
    navigator.clipboard?.writeText(codeStr).then(() => {
      setCopied(idx);
      setTimeout(() => setCopied(null), 2000);
    });
  }

  return (
    <div style={styles.page}>

      {/* Header */}
      <header style={styles.header}>
        <div style={{ display: "flex", alignItems: "center", gap: "16px", flexWrap: "wrap" }}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Dashboard</Link>
          <span style={styles.badge}>Chapter 10 · Projects</span>
        </div>
        <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "0.78rem" }}>
          By <strong style={{ color: PRIMARY }}>Ajao Hazzan</strong> · Full-Stack Software Engineer
        </span>
      </header>

      {/* Hero */}
      <div style={styles.hero}>
        <h1 style={styles.title}>Project: Shopping Cart App</h1>
        <p style={styles.desc}>Build a complete shopping cart application from scratch using React hooks and Context.</p>
      </div>

      {/* Content */}
      <div style={styles.content}>

      /* ── Project Overview ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Project Overview</h2>
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "This project builds a feature-complete shopping cart:" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• Browse a product catalogue with categories and search" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• Add/remove items and adjust quantities" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• Persistent cart (survives page refresh via localStorage)" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• Cart drawer/sidebar with live totals" }} />
        <p style={styles.body} dangerouslySetInnerHTML={{ __html: "• Checkout summary page" }} />
      </div>

      /* ── Project Structure ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Project Structure</h2>
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_1, 0)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 0 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_1)}</pre>
        </div>
      </div>

      /* ── CartContext Implementation ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>CartContext Implementation</h2>
        <div style={{ position: 'relative' }}>
          <button
            onClick={() => copyCode(CODE_2, 1)}
            style={{
              position: 'absolute', top: '10px', right: '10px',
              background: 'rgba(97,218,251,0.15)',
              border: '1px solid rgba(97,218,251,0.3)',
              color: PRIMARY, borderRadius: '6px',
              padding: '4px 10px', cursor: 'pointer',
              fontSize: '0.72rem', fontWeight: 700,
            }}
          >
            {copied === 1 ? '✓ Copied' : 'Copy'}
          </button>
          <pre style={styles.pre}>{String(CODE_2)}</pre>
        </div>
      </div>

      /* ── Challenges to Extend the Project ── */
      <div style={styles.section}>
        <h2 style={styles.sh}>Challenges to Extend the Project</h2>
        <div style={styles.qBox}>
          <div style={styles.qTitle}>📝 CHALLENGES TO EXTEND THE PROJECT</div>
          <div style={styles.qItem}>✦ 1. Add a coupon code system that applies a percentage discount</div>
          <div style={styles.qItem}>✦ 2. Implement a product rating system with stars</div>
          <div style={styles.qItem}>✦ 3. Add a \'Recently Viewed\' section using localStorage</div>
          <div style={styles.qItem}>✦ 4. Add product image zoom on hover</div>
          <div style={styles.qItem}>✦ 5. Integrate with a real API (FakeStoreAPI: https://fakestoreapi.com)</div>
          <div style={styles.qItem}>✦ 6. Add unit tests for the cartReducer function</div>
          <div style={styles.qItem}>✦ 7. Add a stock quantity limit (can\'t add more than stock)</div>
        </div>
      </div>

        {/* Footer */}
        <div style={styles.footer}>
          <Link to="/react-dashboard" style={styles.backBtn}>← Back to React Dashboard</Link>
          <span style={styles.credit}>Mastering React · © 2024 Ajao Hazzan</span>
        </div>
      </div>

    </div>
  );
}
